import {createSlice} from '@reduxjs/toolkit';
import {REHYDRATE} from 'redux-persist';
import {RootState} from './Store';

const initialState = {
  messageListGenie: [] as any[],
  convoIdGenie: null,
  questionText: '',
  contextOutputGenie: [] as any[][],
  lastQuestionsGenie: [] as string[],
};

// A message left mid-flight (isLoading: true) when the tab closes or
// refreshes gets persisted as-is by redux-persist, so on reload it would
// stay stuck "in progress" forever (stop button shown with nothing running).
// Resolve any such message to an interrupted state as soon as it rehydrates.
const resolveStuckLoadingMessages = (messages: any[] | undefined): any[] =>
  Array.isArray(messages)
    ? messages.map((msg) =>
        msg?.isLoading
          ? {
              ...msg,
              isLoading: false,
              text: 'Request interrupted. Please try again.',
              content: 'Request interrupted. Please try again.',
              isError: true,
            }
          : msg,
      )
    : messages || [];

export const ChatBotSliceGenie = createSlice({
  name: 'chatBotGenie',
  initialState,
  reducers: {
    setmessageListGenie: (state, action) => {
      state.messageListGenie = action.payload;
    },

    // Live-updates the placeholder bot message's text as `token` events
    // arrive, while it's still isLoading, so the UI can show the streamed
    // answer in progress instead of a generic loading message.
    updateStreamingMessageGenie(state, action) {
      const {requestId, streamText} = action.payload;
      const messageIndex = state.messageListGenie.findIndex(
        (msg) => msg?.request_id === requestId,
      );
      if (messageIndex !== -1) {
        state.messageListGenie[messageIndex] = {
          ...state.messageListGenie[messageIndex],
          streamText,
        };
      }
    },

    updateMessageListGenie(state, action) {
      const {requestId, finalBotMessage} = action.payload;
      const messageIndex = state.messageListGenie.findIndex(
        (msg) => msg?.request_id === requestId,
      );
      if (messageIndex !== -1) {
        // Update the existing placeholder message with the new data
        state.messageListGenie[messageIndex] = {
          ...state.messageListGenie[messageIndex],
          ...finalBotMessage,
          streamText: undefined,
          isLoading: false, // Ensure loading is set to false
        };
      } else {
        state.messageListGenie.push({...finalBotMessage, isLoading: false});
      }
    },
    setContextOutputGenie(state, action) {
      const newData = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      if (!newData || newData.length === 0) return;
      state.contextOutputGenie = [
        ...(state?.contextOutputGenie || []),
        newData,
      ].slice(-5); // Keep only the latest 5 context outputs
    },

    // Append a user question to the rolling buffer of the last 5 questions.
    // Used to build a numbered context prefix for the next API call.
    addLastQuestionGenie(state, action) {
      const q = typeof action.payload === 'string' ? action.payload.trim() : '';
      if (!q) return;
      state.lastQuestionsGenie = [...(state.lastQuestionsGenie || []), q].slice(
        -5,
      );
    },

    setConvoIdGenie: (state, action) => {
      state.convoIdGenie = action.payload;
    },
    setQuestionGenie: (state, action) => {
      state.questionText = action.payload;
    },
    setFeedbackGenie: (state, action) => {
      const {request_id, is_liked, is_disliked} = action.payload;
      state.messageListGenie = state.messageListGenie.map((msg: any) => {
        if (msg?.request_id === request_id) {
          return {
            ...msg,
            is_liked: is_liked ? true : null,
            is_disliked: is_disliked ? true : null,
          };
        }
        return msg;
      });
    },
    clearChatSliceGenie: (state) => {
      state.messageListGenie = [] as any;
      state.convoIdGenie = null;
      state.questionText = '';
      state.contextOutputGenie = [] as any[][];
      state.lastQuestionsGenie = [] as string[];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state) => {
      state.messageListGenie = resolveStuckLoadingMessages(state.messageListGenie);
    });
  },
});

export const {
  setmessageListGenie,
  clearChatSliceGenie,
  setConvoIdGenie,
  updateMessageListGenie,
  updateStreamingMessageGenie,
  setQuestionGenie,
  setContextOutputGenie,
  addLastQuestionGenie,
  setFeedbackGenie,
} = ChatBotSliceGenie.actions;

export const getmessageListGenie = (state: RootState) =>
  state.chatSliceGenie.messageListGenie;

export const getConvoIdGenie = (state: RootState) =>
  state.chatSliceGenie.convoIdGenie;

export const getQuestionGenie = (state: RootState) =>
  state.chatSliceGenie.questionText;
export const getContextOutputGenie = (state: RootState) =>
  state.chatSliceGenie.contextOutputGenie;
export const getLastQuestionsGenie = (state: RootState) =>
  state.chatSliceGenie.lastQuestionsGenie;
export default ChatBotSliceGenie.reducer;
